import React, { useState, useEffect } from "react";

// Modules

// App
import Layout from "../layouts/item";
import { getThemeJson } from "../../../../utils/helpers";

// UI components
import { Container, Row, Col } from "react-bootstrap";
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";

export default function AddSlide(props) {
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Unknown error. Please refresh and try again."
  );

  const [layoutFilter, setLayoutFilter] = useState(
    props.slideLayout ? props.slideLayout.category : "all"
  );

  const [layouts, setLayouts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!props.samplePresentationLoading) {
      if (props.presentation) {
        try {
          const theme_json = getThemeJson(props?.presentation?.theme?.label);

          setCategories(theme_json?.layoutCategories);

          let slides = [];

          if (props.samplePresentation) {
            theme_json?.layouts?.forEach((item) => {
              let version = 1;

              if (props.samplePresentation?.slides?.length > 0) {
                props.samplePresentation?.slides.forEach((slide) => {
                  try {
                    const json = JSON.parse(slide.slide_json);
                    if (json.reveal.slideLayout.id === item.id) {
                      slides.push({
                        ...item,
                        slide: slide,
                        version: version++,
                      });
                    }
                  } catch (error) {
                    // nothing to see here
                  }
                });
              } else {
                slides.push({
                  ...item,
                  version: version++,
                });
              }
            });

            setLayouts(slides);
            setLoading(false);
            setError(false);
          } else {
            let showEmptySlides = false;

            if (props.user) {
              props.user.current_user.roles.forEach((role) => {
                if (role === "administrator") {
                  showEmptySlides = true;
                }
              });
            }

            if (showEmptySlides) {
              theme_json?.layouts?.forEach((item) => {
                let version = 1;

                slides.push({
                  ...item,
                  version: version++,
                });
              });

              setError(false);
              setLayouts(slides);
              setLoading(false);
            } else {
              setError(true);
              setErrorMessage("Network error. Please refresh and try again");
              setLoading(false);
            }
          }
        } catch (error) {
          setLoading(false);
          setError(true);
          setErrorMessage("Unknown error. Please refresh and try again.");
        }
      } else {
        setLoading(false);
        setErrorMessage(
          "Failed to fetch presentation. Please refresh and try again."
        );
        setError(true);
      }
    }
  }, [
    props.samplePresentation,
    props.presentation,
    props.user,
    props.samplePresentationLoading,
  ]);

  const hasExclusiveLayouts = (category) => {
    const foundLayouts = layouts.filter((layout) => {
      if (Array.isArray(layout.category)) {
        return layout.category.includes(category);
      } else if (typeof layout.category === "string") {
        return layout.category === category;
      }
    });
    const regex = new RegExp(`\\(${category}\\)`);
    const hasCategoryInParentheses = foundLayouts.some((layout) =>
      regex.test(layout.slide.title)
    );
    return hasCategoryInParentheses;
  };

  const filterLayoutCategory = (filter, category, layout) => {
    const isCategoryArray = Array.isArray(category);
    const exclusiveLayouts = hasExclusiveLayouts(filter);
    const regex = new RegExp(`\\(${filter}\\)`);
    if (isCategoryArray) {
      if (exclusiveLayouts) {
        return !(category.includes(filter) && regex.test(layout.slide.title));
      } else {
        return !category.includes(filter);
      }
    } else {
      return filter !== category;
    }
  };

  return (
    <Dialog
      fullScreen={true}
      open={props.fromLayoutOpen}
      onClose={props.onLayoutClose}
    >
      <AppBar
        sx={{
          position: "relative",
          backgroundColor: "primary.light",
          color: "solid.dark",
        }}
      >
        <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography variant="h3" owner="admin" color="adminText.dark">
              {props.title}
            </Typography>
            <Button
              variant="outlined"
              owner="admin"
              dialogbtn="true"
              dialogvariant="cancel"
              edge="start"
              onClick={props.onLayoutClose}
              aria-label="close"
            >
              <Typography variant="button" owner="admin" className="lh">
                Cancel
              </Typography>
            </Button>
          </Toolbar>
        </DialogContent>
      </AppBar>

      {props.samplePresentationLoading ? (
        <DialogContent sx={{ textAlign: "center" }}>
          <CircularProgress color="primary" />
        </DialogContent>
      ) : (
        <>
          {isLoading ? (
            <DialogContent sx={{ textAlign: "center" }}>
              <CircularProgress color="primary" />
            </DialogContent>
          ) : (
            <>
              {isError ? (
                <DialogContent>
                  <Container fluid>
                    <Row className="mb-5">
                      <Col>
                        <Typography variant="body">{errorMessage}</Typography>
                        <br />
                        <br />
                        <Button
                          onClick={() => {
                            window.location.reload();
                          }}
                          variant="contained"
                          color="primary"
                        >
                          Refresh
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </DialogContent>
              ) : (
                <Container fluid>
                  <Row className="mb-5">
                    <Col>
                      <Tabs
                        value={layoutFilter}
                        onChange={(event, newValue) => {
                          setLayoutFilter(newValue);
                        }}
                        aria-label="Layout categories"
                      >
                        <Tab key="all" value="all" label={"All"} />
                        {categories.map((category, index) => (
                          <Tab
                            key={`label-${index}`}
                            value={category}
                            label={category}
                          />
                        ))}
                      </Tabs>
                    </Col>
                  </Row>
                  <Row className="d-flex align-items-stretch">
                    {layouts.map((layout, index) => {
                      if (layout.id === "advanced") {
                        if (!props.permissions.access_advanced_layout) {
                          return null;
                        }
                      }

                      if (layout.id === "blank") {
                        if (!props.permissions.access_blank_layout) {
                          return null;
                        }
                      }

                      return (
                        <Layout
                          layout={layout}
                          key={`layout-${index}`}
                          className={`${layout.category} ${
                            layoutFilter !== "all" &&
                            filterLayoutCategory(
                              layoutFilter,
                              layout.category,
                              layout
                            )
                              ? "d-none"
                              : ""
                          }`}
                          onAddSlide={(slide, _layout) => {
                            props.onAddSlide(slide, _layout);
                          }}
                          samplePresentation={props.samplePresentation}
                        />
                      );
                    })}
                  </Row>
                </Container>
              )}
            </>
          )}
        </>
      )}
    </Dialog>
  );
}
